import { motion } from "framer-motion";
import { Link } from "@tanstack/react-router";
import { Phone, Mail, MapPin, ArrowRight, Calendar } from "lucide-react";
import { JSX } from "react/jsx-runtime";

export function ContactCTASection(): JSX.Element {
  return (
    <section className="py-24 bg-[#1A1A1A] relative overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white leading-tight mb-6">
              Let's Solve Your
              <br />
              <span className=" text-[#C9A84C]">Tax Challenges</span>
              <br />
              Together
            </h2>
            <p className="text-white/60 leading-relaxed mb-10">
              Whether you need urgent compliance help, strategic tax planning,
              or simply want to understand your options — our team is ready to
              help.
            </p>

            <div className="space-y-4 mb-10">
              {[
                {
                  icon: Phone,
                  label: "Call Us",
                  value: "+254 703 173405",
                  href: "tel:+254703173405",
                },
                {
                  icon: Mail,
                  label: "Email Us",
                  value: "info@rosewoodconsulting.co.ke",
                  href: "mailto:info@rosewoodconsulting.co.ke",
                },
                {
                  icon: MapPin,
                  label: "Visit Us",
                  value: "Upper Hill, Nairobi",
                  href: "#",
                },
              ].map(({ icon: Icon, label, value, href }) => (
                <a
                  key={label}
                  href={href}
                  className="flex items-center gap-4 group"
                >
                  <div className="w-11 h-11 bg-[#6B1E2A] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9A84C] transition-colors">
                    <Icon size={17} className="text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-white/40 font-medium">{label}</p>
                    <p className="text-sm font-semibold text-white group-hover:text-[#C9A84C] transition-colors">
                      {value}
                    </p>
                  </div>
                </a>
              ))}
            </div>

            <div className="flex items-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#C9A84C] text-[#1A1A1A] font-semibold rounded-xl hover:bg-[#E2C06B] transition-colors shadow-lg shadow-[#C9A84C]/20"
              >
                <Calendar size={16} />
                Book Consultation
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 text-white font-semibold rounded-xl hover:border-white/40 transition-colors"
              >
                Send Message
                <ArrowRight size={15} />
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="bg-white/5 rounded-2xl p-8 backdrop-blur-sm"
          >
            <h3 className="font-serif text-2xl font-semibold text-white mb-2">
              Quick Enquiry
            </h3>
            <p className="text-white/50 text-sm mb-7">
              We'll respond within 2 business hours.
            </p>
            <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-white/50 mb-1.5 block">
                    First Name
                  </label>
                  <input
                    type="text"
                    placeholder="John"
                    className="w-full px-4 py-2.5 bg-white/8 border border-white/10 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#C9A84C]/40 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-white/50 mb-1.5 block">
                    Last Name
                  </label>
                  <input
                    type="text"
                    placeholder="Doe"
                    className="w-full px-4 py-2.5 bg-white/8 border border-white/10 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#C9A84C]/40 transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-white/50 mb-1.5 block">
                  Email Address
                </label>
                <input
                  type="email"
                  placeholder="john@company.com"
                  className="w-full px-4 py-2.5 bg-white/8 border border-white/10 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#C9A84C]/40 transition-colors"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-white/50 mb-1.5 block">
                  Service Needed
                </label>
                <select className="w-full px-4 py-2.5 bg-white/8 border border-white/10 rounded-lg text-sm text-white/70 focus:outline-none focus:border-[#C9A84C]/40 transition-colors">
                  <option value="" className="bg-[#1A1A1A]">
                    Select a service...
                  </option>
                  <option className="bg-[#1A1A1A]">KRA Registration</option>
                  <option className="bg-[#1A1A1A]">Tax Returns Filing</option>
                  <option className="bg-[#1A1A1A]">eTIMS Compliance</option>
                  <option className="bg-[#1A1A1A]">
                    Bookkeeping & Payroll
                  </option>
                  <option className="bg-[#1A1A1A]">Tax Advisory</option>
                  <option className="bg-[#1A1A1A]">KRA Audit Defence</option>
                  <option className="bg-[#1A1A1A]">International Tax</option>
                  <option className="bg-[#1A1A1A]">Tax Training</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-white/50 mb-1.5 block">
                  Message
                </label>
                <textarea
                  placeholder="Tell us about your tax situation..."
                  rows={3}
                  className="w-full px-4 py-2.5 bg-white/8 border border-white/10 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#C9A84C]/40 transition-colors resize-none"
                />
              </div>
              <button
                type="submit"
                className="w-full py-3 bg-[#C9A84C] text-[#1A1A1A] font-semibold rounded-xl hover:bg-[#E2C06B] transition-colors text-sm"
              >
                Send Enquiry →
              </button>
              <p className="text-xs text-white/30 text-center">
                Your information is kept completely confidential.
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
